import { Component, ElementRef, Inject, OnInit, PLATFORM_ID, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { Alert } from 'src/app/interfaces/alert';
import { ProductType } from 'src/app/interfaces/user';
import { errorMessagesMap } from 'src/app/constants/error-messages';
import { AuthManagerService } from 'src/app/services/auth-manager/auth-manager.service';
import { CheckoutService } from 'src/app/services/checkout/checkout.service';
import { ConfigService } from 'src/app/services/config/config.service';
import { FeatureFlagsService } from 'src/app/services/feature-flag/feature-flag.service';
import { PaymentConfigService } from 'src/app/services/payment-config/payment-config.service';
import { MemberShipOptionsService } from 'src/app/services/membership-options/membership-options.service';
import { UserService } from 'src/app/services/user/user.service';
import { PlansPageConfig } from 'src/app/interfaces/pageConfig';
import { environment } from 'src/environments/environment';
import { PricingTableSubTypes } from 'src/app/shared/components/pricing-table/pricing-table.component';
import { IdentialertSubSelectionModalComponent } from 'src/app/shared/components/subscription-pages/identialert-sub-selection-modal/identialert-sub-selection-modal.component';
import { SmartTagLimit, SmartTagOrderItem } from 'src/app/interfaces/smart-tag';
import { SmartTagService } from 'src/app/services/smart-tag/smart-tag.service';
import { isPlatformBrowser } from '@angular/common';
import { LogService } from 'src/app/services/common/log.service';
import { PetService } from 'src/app/services/pet/pet.service';

/**
 * The rebranded "Our plans" page.
 * Choose Plan and Upgrade Account render exactly the
 * same thing, so both wrappers point here.
 */
@Component({
  selector: 'app-plans-page',
  templateUrl: './plans-page.component.html',
  styleUrl: './plans-page.component.scss',
})
export class PlansPageComponent implements OnInit {
  public alert: Alert = null;
  public loading = false;
  private isBrowser: boolean;
  public membershipSelectionTitle: string;
  public isIdentiSmartTagPlanAvailable: boolean;
  public PricingTableSubTypes = PricingTableSubTypes;
  public petName: string;

  // True (enabled) until a signed-in user's current plan says otherwise.
  public identibasePlusAvailable = true;
  public identiAlertAvailable = true;
  public smartTagSubAvailable = true;
  private limitFailed: boolean = false;
  public showSmartTagPicker: boolean = false;
  public tagPricing: SmartTagLimit | null = null;
  public tagUnitPrice: number;


  public page: PlansPageConfig;
  public basePlanSubtitle: string;
  public alertPlanSubtitle: string;
  public locatorPlanSubtitle: string;

  public plan1YearlyPrice: string;
  public plan2YearlyPrice: string;
  public plan2MonthlyPrice: string;
  public plan3YearlyPrice: string;
  public plan3YearlyPriceOriginal: string;

  private plan1Route: string;
  private plan2Route: string;
  private plan3Route: string;
  private cancelUrl: string = "choose-plan";

  @ViewChild('smartTagPicker') private smartTagPicker?: ElementRef<HTMLElement>;

  constructor(
    private _router: Router,
    private _logService: LogService,
    private _activatedRoute: ActivatedRoute,
    private authManager: AuthManagerService,
    private checkoutService: CheckoutService,
    private configService: ConfigService,
    private petService: PetService,
    private featureFlagService: FeatureFlagsService,
    private paymentConfigService: PaymentConfigService,
    private membershipOptionsService: MemberShipOptionsService,
    private smartTagService: SmartTagService,
    private modalService: NgbModal,
    private userService: UserService,
    @Inject(PLATFORM_ID) private platformId: Object,
  ) {
    this.isBrowser = isPlatformBrowser(this.platformId);
    this.membershipSelectionTitle = this.configService.getTextFromKey('membership-selection-title');

    this.page = this.configService.getPage('plans');
    this.basePlanSubtitle = this.withPetName(this.page.basePlan.subtitle);
    this.locatorPlanSubtitle = this.withPetName(this.page.locatorPlan.subtitle);
    this.alertPlanSubtitle = this.withPetName(this.page.alertPlan.subtitle);

    this.plan1Route = this.configService.getTextFromKey('1-plan-route');
    this.plan2Route = this.configService.getTextFromKey('2-plan-route');
    this.plan3Route = this.configService.getTextFromKey('3-plan-route');

    this.plan1YearlyPrice = this.configService.getTextFromKey('1-plan-yearly-price');
    this.plan2YearlyPrice = this.configService.getTextFromKey('2-plan-yearly-price');
    this.plan3YearlyPrice = this.configService.getTextFromKey('3-plan-yearly-price');
    this.plan3YearlyPriceOriginal = this.configService.getTextFromKey('3-plan-yearly-price-original');

    // Same price source as the membership options step, so the two pages can't disagree.
    // Falls back to config when the backend offers no price (signed-out visitors included).
    this.plan1YearlyPrice =
      this.paymentConfigService.getYearlyDisplayPrice(ProductType.BasicPlus) ?? this.plan1YearlyPrice;
    this.plan2YearlyPrice =
      this.paymentConfigService.getYearlyDisplayPrice(ProductType.Premium) ?? this.plan2YearlyPrice;
    // Null when the backend offers Alert yearly-only, which skips the billing-interval modal.
    this.plan2MonthlyPrice = this.paymentConfigService.getMonthlyDisplayPrice(
      ProductType.Premium,
      this.configService.getTextFromKey('2-plan-monthly-price')
    );
    this.plan3YearlyPrice = this.paymentConfigService.getYearlyDisplayPrice(ProductType.Locator) ?? this.plan3YearlyPrice;
    this.tagUnitPrice = this.configService.getNumberFromKey('3-plan-tag-unit-price');

    this.isIdentiSmartTagPlanAvailable = this.featureFlagService.isEnabled('isIdentiSmartTagPlanAvailable');
  }

  ngOnInit(): void {
    const openLocator =this._activatedRoute.snapshot.queryParamMap.get('plan') === 'locator' && this.isIdentiSmartTagPlanAvailable;
    if (!this.authManager.isAuthenticated) {
      if (openLocator) this.selectLocator();
      return
    };

    if (this.isBrowser && this.isIdentiSmartTagPlanAvailable) {
      this.loadSmartTagLimit();
    }

    this.userService.getMe().subscribe((user) => {
      this.petName = this.petService.createPetsListString(user.pets);
      this.identibasePlusAvailable = this.membershipOptionsService.identiBasePlusAvailable(user);
      this.identiAlertAvailable = this.membershipOptionsService.identiAlertAvailable(user);
      this.smartTagSubAvailable = this.membershipOptionsService.smartTagSubAvailable(user);
      this.basePlanSubtitle = this.withPetName(this.page.basePlan.subtitle);
      this.alertPlanSubtitle = this.withPetName(this.page.alertPlan.subtitle);
      this.locatorPlanSubtitle = this.withPetName(this.page.locatorPlan.subtitle);

      if (openLocator) this.selectLocator();
    });
  }

  // Users mid-registration may have no pet yet, so the placeholder needs a fallback.
  private withPetName(text: string): string {
    return text?.replace('{{petName}}', this.petName || 'Your pet');
  }

  public selectBase(): void {
    this.closeAlert();
    this.showSmartTagPicker = false;
    this.checkoutPlan(environment.stripePlans.identibasePlus, this.plan1Route);
  }

  /**
   * Alert is the only plan with a billing-interval choice, so it opens the selection modal
   * instead of checking out directly. When the backend offers no monthly price the modal would
   * hold a single option, so we fall through to the annual checkout.
   */
  public selectAlert(): void {
    this.closeAlert();
    this.showSmartTagPicker = false;

    if (!this.authManager.isAuthenticated) {
      this._router.navigate(['/login-register'], { queryParams: { r: this.plan2Route } });
      return;
    }

    if (!this.plan2MonthlyPrice) {
      this.checkoutPlan(environment.stripePlans.identiAlertAnnual, this.plan2Route);
      return;
    }

    const modalRef = this.modalService.open(IdentialertSubSelectionModalComponent, {
      backdrop: 'static',
      centered: true,
      size: 'md',
    });
    modalRef.componentInstance.modalRef = modalRef;
  }

  public selectLocator(): void {
    if (!this.smartTagSubAvailable) return;
    this.closeAlert();

    this.showSmartTagPicker = true;
    this.scrollToSmartTagPicker();

    // Without the allowance the configurator stays locked.
    if (this.limitFailed) this.showGenericError();
  }

  // Signed-out visitors are sent to log in first, then back to the plan's product page.
  private checkoutPlan(planId: string, productPageRoute: string): void {
    if (!this.authManager.isAuthenticated) {
      this._router.navigate(['/login-register'], { queryParams: { r: productPageRoute } });
      return;
    }

    this.loading = true;
    this.checkoutService
      .checkout([planId])
      .subscribe({
        error: () => {
          this.alert = { type: 'danger', message: errorMessagesMap.generic_error_message };
        },
      })
      .add(() => {
        this.loading = false;
      });
  }

  public navigateToProductPage(pricingTableSubType: PricingTableSubTypes): void {
    switch (pricingTableSubType) {
      case PricingTableSubTypes.IDENTIBASEPLUS:
        this._router.navigate([this.plan1Route]);
        break;
      case PricingTableSubTypes.IDENTIALERT:
        this._router.navigate([this.plan2Route]);
        break;
      case PricingTableSubTypes.SMARTTAG:
        this._router.navigate([this.plan3Route]);
        break;
    }
  }

  public closeAlert(): void {
    this.alert = null;
  }

  private loadSmartTagLimit(): void {
    this.smartTagService.getSmartTagLimit().subscribe({
      next: (limit) => this.tagPricing = limit,
      error: (err) => {
        this.limitFailed = true;
        this._logService.error(errorMessagesMap.smart_tag_limit_error, err);
        // The keeper may already be looking at the locked picker.
        if (this.showSmartTagPicker) this.showGenericError();
      },
    });
  }

  private scrollToSmartTagPicker(): void {
    if (!this.isBrowser) return;
    setTimeout(() => this.smartTagPicker?.nativeElement.scrollIntoView({ behavior: 'smooth', block: 'start' }));
  }

  private showGenericError(): void {
    this.alert = { type: 'danger', message: errorMessagesMap.generic_error_message };
  }

    /** Buys the Locator plan on its own, without any tag. */
  public checkoutLocatorOnly(): void {
    if (!this.requireLogin() || !this.smartTagSubAvailable) return;
    this.checkoutService
      .checkout([environment.stripePlans.smartTagSubscription], { cancelUrl: this.cancelUrl })
      .subscribe({ error: () => this.showGenericError() });
  }


  /** Every tag is covered by the plan, so the session only pays for the subscription. */
  public checkoutLocatorWithFreeTags(tags: SmartTagOrderItem[]): void {
    this.checkoutLocatorWithTags([environment.stripePlans.smartTagSubscription], tags);
  }

  /** Some tags are extra. The tag price goes in once, the backend counts the paid ones. */
  public checkoutLocatorWithPaidTags(tags: SmartTagOrderItem[]): void {
    this.checkoutLocatorWithTags(
      [environment.stripePlans.smartTagSubscription, environment.stripePlans.smartTagPhysicalProduct],
      tags
    );
  }

  private checkoutLocatorWithTags(pricingPlanIds: string[], tags: SmartTagOrderItem[]): void {
    if (!this.requireLogin() || !this.smartTagSubAvailable) return;
    this.checkoutService
      .checkout(pricingPlanIds, { smartTag: { tags }, cancelUrl: this.cancelUrl })
      .subscribe({ error: () => this.showGenericError() });
  }

  private requireLogin(): boolean {
    if (this.authManager.isAuthenticated) return true;
    this._router.navigate(['/login-register'], {
      queryParams: { r: '/choose-plan?plan=locator' },
    });
    return false;
  }
}
